import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  FolderKanban, Users, MessageSquare, FileUp, BarChart3, Clock,
  MailCheck, MessageCircle, HardDrive, StickyNote, CalendarDays,
  AlertTriangle, Brain, Frown, Timer, Layers, CheckCircle2,
  UserPlus, ListTodo, Upload, Eye, BellRing, Target,
  Sparkles, Palette, Globe, PenTool, MonitorSmartphone, Briefcase,
  ArrowRight, Zap, Shield
} from 'lucide-react';

const BRAND = '#155179';

const Discover = () => {
  const navigate = useNavigate();

  const painTools = [
    { icon: MailCheck, label: 'Emails' },
    { icon: MessageCircle, label: 'WhatsApp' },
    { icon: HardDrive, label: 'Google Drive' },
    { icon: Layers, label: 'Trello' },
    { icon: StickyNote, label: 'Notion' },
    { icon: Brain, label: 'Vos notes' },
    { icon: CalendarDays, label: 'Votre agenda' },
  ];

  const painPoints = [
    { icon: AlertTriangle, text: 'Vous oubliez des tâches' },
    { icon: BellRing, text: 'Vous relancez constamment' },
    { icon: Eye, text: 'Vous cherchez les informations' },
    { icon: Layers, text: 'Vous multipliez les outils' },
    { icon: Timer, text: 'Vous stressez pour les délais' },
  ];

  const features = [
    { icon: FolderKanban, text: 'Créer un projet en 10 secondes' },
    { icon: UserPlus, text: 'Inviter vos collaborateurs' },
    { icon: ListTodo, text: 'Attribuer les tâches' },
    { icon: MessageSquare, text: 'Discuter dans un chat interne' },
    { icon: Upload, text: 'Déposer les fichiers' },
    { icon: BarChart3, text: 'Suivre l\'avancement en temps réel' },
    { icon: BellRing, text: 'Voir les clients à relancer' },
    { icon: Eye, text: 'Voir ce qui avance' },
    { icon: CheckCircle2, text: 'Ne plus rien oublier' },
  ];

  const targets = [
    { icon: UserPlus, label: 'Assistant(e) administratif(ve)' },
    { icon: FolderKanban, label: 'Gestionnaire de projet' },
    { icon: Sparkles, label: 'Freelance' },
    { icon: Briefcase, label: 'Entrepreneur / TPE' },
    { icon: Globe, label: 'Coach / Formateur' },
    { icon: MonitorSmartphone, label: 'Agent immobilier' },
    { icon: PenTool, label: 'Artisan' },
    { icon: Palette, label: 'Community manager' },
    { icon: Target, label: 'Consultant' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: BRAND }}>
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-lg font-bold text-slate-900">CREA FLOW</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600">Connexion</Button>
            <Button onClick={() => navigate('/pricing')} style={{ backgroundColor: BRAND }} className="text-white hover:opacity-90">
              Essayer maintenant
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full mb-8" style={{ backgroundColor: BRAND + '12', color: BRAND }}>
            <Zap className="w-4 h-4" />
            L'outil simple pour piloter vos projets et votre quotidien
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight">
            Organisez, suivez et pilotez<br />
            <span style={{ color: BRAND }}>vos projets</span><br />
            simplement
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            CREA FLOW centralise tout au même endroit pour suivre vos projets du début à la fin, pas à pas.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate('/pricing')} style={{ backgroundColor: BRAND }} className="text-white hover:opacity-90 px-8 h-12 text-base">
              Commencer maintenant <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <span className="text-sm text-slate-500">Gratuit jusqu'à 5 projets — 9,99€/mois au-delà</span>
          </div>
        </div>
      </section>

      {/* Problem - Title */}
      <section className="pt-16 sm:pt-20 pb-6 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Votre organisation vous fait perdre du temps
          </h2>
        </div>
      </section>

      {/* Problem - Tools (aéré, grandes pills) */}
      <section className="py-10 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-lg text-slate-600 mb-8">Vous passez votre journée entre :</p>
          <div className="flex flex-wrap justify-center gap-4">
            {painTools.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 bg-white rounded-2xl px-6 py-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <Icon className="w-5 h-5" style={{ color: BRAND + '80' }} />
                <span className="text-base text-slate-700 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem - Pain points (grandes cartes roses aérées) */}
      <section className="py-10 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-xl sm:text-2xl font-semibold text-slate-700 mb-10">
            Et au final… vous perdez du temps.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {painPoints.map(({ icon: Icon, text }) => (
              <div key={text} className="bg-red-50/70 border border-red-100 rounded-2xl p-6 text-center hover:shadow-sm transition-shadow">
                <Icon className="w-7 h-7 text-red-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700 leading-snug">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem - Results */}
      <section className="pb-16 sm:pb-20 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-2xl mx-auto mt-10">
          <div className="rounded-2xl p-6 sm:p-8 text-white" style={{ backgroundColor: BRAND }}>
            <h3 className="text-lg font-semibold mb-4 text-center">Résultat :</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['Moins de clarté', 'Moins d\'organisation', 'Moins de professionnalisme', 'Et parfois… des clients insatisfaits.'].map((result, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Frown className="w-4 h-4 text-red-300 flex-shrink-0" />
                  <span className="text-slate-200 text-sm">{result}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              Un seul outil pour piloter vos projets clients de A à Z
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Avec CREA FLOW vous pouvez :
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map(({ icon: Icon, text }) => (
              <Card key={text} className="p-5 flex items-start gap-4 hover:shadow-md transition-shadow border-slate-200">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: BRAND + '12' }}>
                  <Icon className="w-5 h-5" style={{ color: BRAND }} />
                </div>
                <p className="text-sm font-medium text-slate-700 pt-2">{text}</p>
              </Card>
            ))}
          </div>
          <p className="mt-8 text-center text-lg font-semibold" style={{ color: BRAND }}>Tout est au même endroit.</p>
        </div>
      </section>

      {/* Simple */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-4">Simple à utiliser</h2>
          <p className="text-lg text-slate-600 mb-2">Pas besoin de formation.</p>
          <p className="text-lg text-slate-600 mb-10">Pas besoin de configuration complexe.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            {[{ n: '1', t: 'Vous créez un projet' }, { n: '2', t: 'Vous invitez votre équipe' }, { n: '3', t: 'Vous travaillez' }].map(({ n, t }, i) => (
              <React.Fragment key={n}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full text-white flex items-center justify-center text-lg font-bold" style={{ backgroundColor: BRAND }}>{n}</div>
                  <span className="text-base font-medium text-slate-700">{t}</span>
                </div>
                {i < 2 && <ArrowRight className="w-5 h-5 text-slate-300 hidden sm:block" />}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-10 text-xl font-semibold text-slate-900">C'est tout.</p>
        </div>
      </section>

      {/* Target */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-4">Pensé pour les métiers du web</h2>
          <p className="text-lg text-slate-600 mb-10">
            CREA FLOW est conçu pour ceux qui doivent suivre leurs clients dans leurs projets digitaux :
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {targets.map(({ icon: Icon, label }) => (
              <Card key={label} className="p-5 flex flex-col items-center gap-3 hover:shadow-md transition-shadow border-slate-200">
                <Icon className="w-8 h-8" style={{ color: BRAND }} />
                <span className="text-sm font-medium text-slate-700">{label}</span>
              </Card>
            ))}
          </div>
          <p className="mt-8 text-base text-slate-600">Tous ceux qui gèrent des projets au quotidien.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: BRAND }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-6">
            Moins d'outils. Plus de clarté.<br />Plus d'efficacité.
          </h2>
          <div className="space-y-2 text-slate-300 text-base sm:text-lg mb-10">
            <p>Arrêtez de chercher vos informations partout.</p>
            <p>Centralisez vos projets.</p>
            <p>Suivez vos clients simplement.</p>
            <p className="font-semibold text-white">Livrez plus vite.</p>
          </div>
          <Button size="lg" onClick={() => navigate('/pricing')} className="bg-white text-slate-900 hover:bg-slate-100 px-8 h-12 text-base font-semibold">
            Commencer maintenant — 9,99€/mois <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: BRAND }}>
              <span className="text-white font-bold text-xs">C</span>
            </div>
            <span className="text-sm font-semibold text-slate-700">CREA FLOW</span>
            <span className="text-sm text-slate-400">par CREA DIGI</span>
          </div>
          <p className="text-sm text-slate-400">© {new Date().getFullYear()} CREA FLOW — CREA DIGI. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Discover;
